"use client";

import { useRouter } from "next/navigation";
import { Divider, MenuItem, TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import { carteiraPath, type CarteiraPrimaryTab } from "@/libs/carteira-route";

type Props = {
  activePortfolioId: string;
  activeTab: string;
  newHref: string;
  label?: string;
  size?: "small" | "medium";
  minWidth?: number;
  sx?: SxProps<Theme>;
  portfolios: Array<{
    id: string;
    name: string;
  }>;
};

const NEW_PORTFOLIO_VALUE = "__new__";

export function PortfolioSwitcher({
  activePortfolioId,
  activeTab,
  newHref,
  label,
  size = "small",
  minWidth = 240,
  sx,
  portfolios,
}: Props) {
  const router = useRouter();

  return (
    <TextField
      select
      label={label}
      value={activePortfolioId}
      size={size}
      aria-label={label ?? "Carteira ativa"}
      sx={
        [
          {
            minWidth,
            "& .MuiInputLabel-root": {
              display: "none",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: 999,
              minHeight: size === "small" ? 28 : 34,
              backgroundColor: "transparent",
              color: "inherit",
              fontSize: size === "small" ? 12 : 14,
              fontWeight: 600,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "currentColor",
              opacity: 0.38,
            },
            "& .MuiSelect-select": {
              py: size === "small" ? "4px !important" : undefined,
              pr: "28px !important",
              fontSize: size === "small" ? "12px" : undefined,
              fontWeight: 600,
              color: "inherit",
            },
            "& .MuiSvgIcon-root": {
              color: "inherit",
              fontSize: size === "small" ? 16 : undefined,
            },
          },
          ...(sx ? [sx] : []),
        ] as SxProps<Theme>
      }
      onChange={(event) => {
        const value = event.target.value;

        if (value === NEW_PORTFOLIO_VALUE) {
          router.push(newHref);
          return;
        }

        router.push(
          carteiraPath.tab(
            value,
            (activeTab as CarteiraPrimaryTab) ?? "resumo",
          ),
        );
      }}
    >
      {portfolios.map((portfolio) => (
        <MenuItem key={portfolio.id} value={portfolio.id}>
          {portfolio.name}
        </MenuItem>
      ))}
      <Divider />
      <MenuItem value={NEW_PORTFOLIO_VALUE}>Nova carteira</MenuItem>
    </TextField>
  );
}
